// ABOUTME: API client for the Nervous System gateway using cookie-session auth.
// ABOUTME: Holds the short-lived access token in memory; refresh token lives in an httpOnly cookie.

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:7777';

// Cached (non-sensitive) user for instant UI on reload. Re-validated on boot
// via silent refresh. The access token is NEVER persisted here.
export interface CachedUser {
  id: string;
  email: string;
  org_id: string;
  role: string;
}

// --- In-memory access token store -------------------------------------------
// The access token is deliberately kept in a module-level variable so it never
// touches localStorage. It is repopulated on every reload by a silent refresh
// against the httpOnly refresh cookie.
let accessToken: string | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

// --- Cached user (non-sensitive) --------------------------------------------
export function getStoredUser(): CachedUser | null {
  const raw = localStorage.getItem('ns_user');
  if (!raw) return null;
  try {
    return JSON.parse(raw) as CachedUser;
  } catch {
    return null;
  }
}

export function setStoredUser(user: CachedUser): void {
  localStorage.setItem('ns_user', JSON.stringify(user));
}

export function clearStoredAuth(): void {
  accessToken = null;
  localStorage.removeItem('ns_user');
  // Legacy key from the old localStorage-JWT model; remove if lingering.
  localStorage.removeItem('ns_token');
}

// --- JWT payload decode ------------------------------------------------------
export interface AccessClaims {
  user_id?: string;
  org_id?: string;
  email?: string;
  role?: string;
}

// Decode the payload segment of a standard JWT (base64url JSON). This is the
// user's own token, read client-side purely to populate the UI — it is NOT a
// signature check.
export function decodeAccessClaims(token: string): AccessClaims {
  try {
    const payload = token.split('.')[1];
    if (!payload) return {};
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    );
    return JSON.parse(json) as AccessClaims;
  } catch {
    return {};
  }
}

// --- CSRF helper -------------------------------------------------------------
// Reads the JS-readable ns_csrf cookie set by the gateway on /auth responses.
export function getCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)ns_csrf=([^;]*)/);
  const raw = match?.[1];
  return raw !== undefined ? decodeURIComponent(raw) : null;
}

// --- Refresh -----------------------------------------------------------------
// Calls POST /auth/refresh with the CSRF header and cookies. On success stores
// the new access token in memory and returns it; on failure returns null.
// Single-flight: concurrent 401s share one in-flight refresh.
let refreshInFlight: Promise<string | null> | null = null;

export async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      const csrf = getCsrfToken();
      if (csrf) headers['X-CSRF-Token'] = csrf;

      const res = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers,
        credentials: 'include',
      });

      if (!res.ok) return null;

      const data = (await res.json()) as { token: string };
      if (!data.token) return null;

      accessToken = data.token;
      return accessToken;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

// --- Fetch wrapper -----------------------------------------------------------
async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  isRetry = false,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });

  // The auth endpoints (/auth/login, /auth/verify, ...) own their own errors —
  // a 401 there means "bad credentials/token", not "session expired". Don't try
  // to refresh-and-retry or redirect; let the error surface to the caller.
  const isAuthEndpoint = path.startsWith('/auth/');

  if (res.status === 401 && !isRetry && !isAuthEndpoint) {
    // Attempt a single silent refresh, then retry the original request once.
    const newToken = await refreshAccessToken();
    if (newToken) {
      return request<T>(method, path, body, true);
    }
    // Refresh failed — session is gone.
    clearStoredAuth();
    window.location.replace('/login');
    throw new Error('Session expired');
  }

  if (res.status === 401 && !isAuthEndpoint) {
    // Already retried once and still unauthorized.
    clearStoredAuth();
    window.location.replace('/login');
    throw new Error('Session expired');
  }

  if (res.status === 204) return undefined as T;

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || err.message || `HTTP ${res.status}`);
  }

  return res.json();
}

// Exported API methods
export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};
